import { useEffect } from "react";
import { useSwivuBryntumContext } from "@/react/hooks/useSwivuBryntumContext";
import useViews from "@/react/hooks/useViews";
import { VIEW_TYPES } from "@/other/constants/OtherKeys.jsx";
import {
  MarketingStackWorkflowStrip,
  marketingStackCardClassName,
  marketingStackContentInsetClassName,
  marketingStackPageClassName,
} from "@/react/components/marketingStack";
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from "@/modern/components/ui/resizable";
import { LeadsProvider, useLeadsContext } from "./context/LeadsContext";
import { LeadDetailPanel } from "./components/LeadDetailPanel";
import { LeadCard } from "./components/LeadCard";
import { LeadsToolbar } from "./components/LeadsToolbar";
import { LeadsEmptyState } from "./components/LeadsEmptyState";
import { useFilteredLeads } from "./hooks/useFilteredLeads";
import { WidgetParamKeys } from "@/types/entity";
import { cn } from "@/modern/utils";

function LeadsViewContent() {
  const { entityManager } = useSwivuBryntumContext();
  const viewManagerState = useViews() as {
    selectedView?: { otherParams?: Record<string, unknown> };
  } | null;
  const { selectedSubmission, selectSubmission, clearFilters } = useLeadsContext();
  const { submissions, filteredSubmissions, hasActiveFilters } = useFilteredLeads();

  const isStoreEmpty = submissions.length === 0;
  const viewDeepLinkId =
    viewManagerState?.selectedView?.otherParams?.[WidgetParamKeys.LEADS.EDIT];
  const deepLinkSubmissionId =
    viewDeepLinkId ?? entityManager?.otherParams?.[WidgetParamKeys.LEADS.EDIT];

  // View-nav deep link (e.g. notification → /leads with selectedView.otherParams)
  useEffect(() => {
    if (viewDeepLinkId == null || !submissions.length) return;
    const submission = submissions.find(
      (s) => Number(s.id) === Number(viewDeepLinkId),
    );
    if (submission) selectSubmission(submission);
  }, [viewDeepLinkId, submissions, selectSubmission]);

  // URL deep link (e.g. /leads?submissionId=3 via entityManager query params)
  useEffect(() => {
    entityManager?.callParamAction(WidgetParamKeys.LEADS.EDIT, (id) => {
      if (!submissions.length) return false;
      const submission = submissions.find((s) => Number(s.id) === Number(id));
      if (submission) {
        selectSubmission(submission);
      }
      return true;
    });
  }, [entityManager, submissions, selectSubmission]);

  useEffect(() => {
    if (submissions.length === 0) return;
    if (deepLinkSubmissionId != null) return;
    const newest = submissions[0];
    if (!newest?.createdAt) return;
    const age = Date.now() - new Date(newest.createdAt).getTime();
    if (age > 30_000) return;
    const t = setTimeout(() => {
      const el = document.getElementById(`lead-row-${newest.id}`);
      el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    }, 150);
    return () => clearTimeout(t);
  }, [submissions, deepLinkSubmissionId]);

  return (
    <div className={marketingStackPageClassName}>
      <MarketingStackWorkflowStrip activeView={VIEW_TYPES.leads} />
      <div className={marketingStackContentInsetClassName}>
        <div className={marketingStackCardClassName()}>
          <LeadsToolbar />
          <div className="min-h-0 flex-1 overflow-hidden">
        <ResizablePanelGroup
          key={selectedSubmission ? "leads-split" : "leads-full"}
          orientation="horizontal"
          className="h-full min-w-0"
        >
          <ResizablePanel id="leads-list" maxSize={selectedSubmission ? "75%" : "100%"}>
            <div className="@container/leads-list h-full min-w-0 overflow-y-auto">
              <div className="flex min-h-full flex-col">
                {isStoreEmpty ? (
                  <LeadsEmptyState />
                ) : filteredSubmissions.length === 0 ? (
                  <div className="m-4 flex flex-1 flex-col items-center justify-center gap-3 rounded-xl border border-dashed border-border bg-card/50 p-12 text-center">
                    <p className="text-sm font-medium text-foreground">No leads match</p>
                    <p className="text-xs text-muted-foreground">
                      Try a different search or adjust filters above.
                    </p>
                    {hasActiveFilters && (
                      <button
                        type="button"
                        onClick={clearFilters}
                        className="mt-2 rounded-md border border-border bg-background px-3 py-1.5 text-xs font-medium text-foreground hover:bg-muted"
                      >
                        Clear all
                      </button>
                    )}
                  </div>
                ) : (
                  <div
                    className={cn(
                      "grid grid-cols-1 gap-4 p-4",
                      "@[28rem]/leads-list:grid-cols-2",
                      "@[42rem]/leads-list:grid-cols-3",
                      !selectedSubmission && "@[56rem]/leads-list:grid-cols-4",
                    )}
                  >
                    {filteredSubmissions.map((s) => (
                      <LeadCard key={s.id} submission={s} />
                    ))}
                  </div>
                )}
              </div>
            </div>
          </ResizablePanel>

          {selectedSubmission && (
            <>
              <ResizableHandle withHandle />
              <ResizablePanel id="lead-detail" defaultSize="35%" minSize="28%" maxSize="50%">
                <div className="flex h-full min-h-0 flex-col overflow-hidden border-l border-border/70 bg-card">
                  <LeadDetailPanel />
                </div>
              </ResizablePanel>
            </>
          )}
        </ResizablePanelGroup>
          </div>
        </div>
      </div>
    </div>
  );
}

export function LeadsView() {
  return (
    <LeadsProvider>
      <LeadsViewContent />
    </LeadsProvider>
  );
}

export default LeadsView;
